/**
 * Sidebar v2 search: thread matching over the already-ordered sidebar
 * collection, plus the project-scope combobox state.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.logic.ts`
 * (searchSidebarThreads, project scope reducer) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: content matches come in through the
 * same `contentMatchKeys` seam, keyed by `threadSearchMatchKey` (ported
 * alongside from the upstream thread-search module). Forge queries the
 * orchestration RPC for message matches and keeps title/branch matching local.
 */
import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";

import { resolveSettledThreadTimestamp, type SettledThreadTimestampInput } from "./sidebarV2Sort";

const EMPTY_CONTENT_MATCH_KEYS: ReadonlySet<string> = new Set<string>();

/** Stable key joining a content match to its sidebar row. Ported from
 * pingdotgg/t3code packages/client-runtime/src/state/threadSearch.ts at the
 * program pin. */
export function threadSearchMatchKey(
  match: Pick<{ environmentId: EnvironmentId; threadId: ThreadId }, "environmentId" | "threadId">,
): string {
  return JSON.stringify([match.environmentId, match.threadId]);
}

/**
 * Search the already-ordered sidebar thread collection by title or the
 * caller's extra terms (forge passes branch and linked-PR terms), plus any
 * thread whose messages a server search matched (`contentMatchKeys`, keyed
 * by `threadSearchMatchKey`). Keeping the input order means lifecycle
 * ordering (active, snoozed, settled) remains stable while the user narrows
 * the list.
 */
export function searchSidebarThreads<
  T extends {
    readonly environmentId: EnvironmentId;
    readonly id: ThreadId;
    readonly title: string;
  },
>(
  threads: readonly T[],
  query: string,
  contentMatchKeys: ReadonlySet<string> = EMPTY_CONTENT_MATCH_KEYS,
  extraSearchTerms?: (thread: T) => ReadonlyArray<string>,
): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) return [];
  const titleMatches: T[] = [];
  const contentMatches: T[] = [];
  for (const thread of threads) {
    const terms = [thread.title, ...(extraSearchTerms?.(thread) ?? [])];
    const matchesTitle = terms.some((term) => term.toLowerCase().includes(normalizedQuery));
    if (matchesTitle) {
      titleMatches.push(thread);
    } else if (
      contentMatchKeys.size > 0 &&
      contentMatchKeys.has(
        threadSearchMatchKey({ environmentId: thread.environmentId, threadId: thread.id }),
      )
    ) {
      contentMatches.push(thread);
    }
  }
  return [...titleMatches, ...contentMatches];
}

/** Filter projects by a trimmed query, omitting the all-projects item only while filtering. */
export function filterSidebarProjectScopeItems<TItem extends { readonly value: string }>(input: {
  items: readonly TItem[];
  query: string;
  matches: (item: TItem, query: string) => boolean;
}): readonly TItem[] {
  const query = input.query.trim();
  if (query.length === 0) return input.items;
  return input.items.filter((item) => item.value !== "all" && input.matches(item, query));
}

export interface SidebarProjectScopeMenuState {
  readonly open: boolean;
  readonly query: string;
}

export type SidebarProjectScopeMenuAction =
  | { readonly type: "query-changed"; readonly query: string }
  | { readonly type: "open-changed"; readonly open: boolean }
  | { readonly type: "project-settings-opened" };

/** Update the scope-menu query or open state, clearing the query on open changes and settings navigation. */
export function reduceSidebarProjectScopeMenuState(
  state: SidebarProjectScopeMenuState,
  action: SidebarProjectScopeMenuAction,
): SidebarProjectScopeMenuState {
  switch (action.type) {
    case "query-changed":
      return { ...state, query: action.query };
    case "open-changed":
      return { open: action.open, query: "" };
    case "project-settings-opened":
      return { open: false, query: "" };
  }
}

// Settled rows are history, so they order by when the work ENDED, not when
// the thread was created or last touched.
/** Settled rows order by when the work ENDED; malformed stamps sink to 0. */
function settledTimestampMs(thread: SettledThreadTimestampInput): number {
  const timestamp = resolveSettledThreadTimestamp(thread);
  return timestamp === null ? 0 : Date.parse(timestamp);
}

/** Return settled threads newest first, breaking equal settlement times by thread ID. */
export function sortSettledThreadsForSidebar<
  T extends SettledThreadTimestampInput & { readonly id: string },
>(threads: readonly T[]): T[] {
  return threads.toSorted(
    (left, right) =>
      settledTimestampMs(right) - settledTimestampMs(left) || left.id.localeCompare(right.id),
  );
}

/** Extra search terms carrying a thread's linked branch: a null branch
 * contributes none, so title-only threads are never matched by an empty
 * string. */
export function threadBranchSearchTerms(thread: {
  readonly branch: string | null;
}): readonly string[] {
  return thread.branch === null ? [] : [thread.branch];
}

/** Minimal structural keyboard event; the React synthetic event satisfies it. */
export interface ThreadSearchKeyEvent {
  readonly key: string;
  preventDefault(): void;
}

/** Combobox keyboard model for the thread search field: Escape clears the
 * search, ArrowUp and ArrowDown walk the results with wraparound, and
 * Enter activates the active result (the first when the index outran the
 * narrowing list). */
export function handleThreadSearchKeyDown(
  event: ThreadSearchKeyEvent,
  state: {
    readonly resultCount: number;
    readonly activeSearchResultIndex: number;
    readonly onActiveIndexChange: (next: (index: number) => number) => void;
    readonly onSelectResult: (index: number) => void;
    readonly onClearSearch: () => void;
  },
): void {
  if (event.key === "Escape") {
    event.preventDefault();
    state.onClearSearch();
    return;
  }
  if (state.resultCount === 0) return;
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    const delta = event.key === "ArrowDown" ? 1 : -1;
    // Updater form: the setter applies it to the latest index, so a stale
    // render scope cannot replay an old position.
    state.onActiveIndexChange((index) => (index + delta + state.resultCount) % state.resultCount);
  } else if (event.key === "Enter") {
    event.preventDefault();
    state.onSelectResult(
      state.activeSearchResultIndex < state.resultCount ? state.activeSearchResultIndex : 0,
    );
  }
}
